import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { createHospora, HLaunchScreen } from '@hospora/ui'
import App from './App.vue'
import router from './router'
import { HOSPORA_URL, HosporaOrgMissingError, hosporaOrg, setHosporaOrg } from './lib/hospora'
import { useOrgStore } from './stores/org'
import { apiBaseUrl, sharedOrgSlug, storageKey } from './lib/runtime'
// CSS order (see @hospora/ui README): fonts, then the app's own CSS, then the library last.
import '@hospora/ui/fonts.css'
import '../../shared-ui/src/styles/tokens.css'
import './assets/staff.css'
import '@hospora/ui/style.css'
import * as Sentry from '@sentry/vue'
import { isIgnorableBrowserError, sentryEventMessage, suspension, watchOrgSuspension } from '@hospora-bookings/shared-ui'

async function boot() {
  // One place that notices "this organisation is suspended" (402 org_suspended from any API call); App.vue shows the page.
  watchOrgSuspension(apiBaseUrl())
  const hospora = createHospora({ product: 'bookings', theme: 'light', brand: '#1f2937', baseUrl: HOSPORA_URL })
  const launchScreen = window.location.hash.includes('hospora_token=') ? createApp(HLaunchScreen, { productKey: 'bookings' }) : null
  launchScreen?.use(hospora).mount('#app')
  const app = createApp(App)
  const pinia = createPinia()
  app.use(pinia)
  app.use(hospora)

  // Error reporting goes to the deployment's own Sentry project (VITE_SENTRY_DSN). JB & Sons' hostnames keep its
  // project; any other organisation reports nowhere unless it sets its own: never into JB's.
  const JB_SENTRY_DSN = 'https://10511191d6ae0df217ac43fdf0900d4c@o4511733872525313.ingest.de.sentry.io/4511758900789328'
  const onJbHost = /(^|\.)jbandsons\.com\.au$/.test(window.location.hostname) || window.location.hostname === 'jb-venue-staff.pages.dev'
  const sentryDsn = import.meta.env.VITE_SENTRY_DSN || (onJbHost ? JB_SENTRY_DSN : undefined)

  Sentry.init({
    app,
    enabled: !!sentryDsn,
    dsn: sentryDsn,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: 1.0,
    tracePropagationTargets: ['localhost', ...(import.meta.env.VITE_API_URL ? [import.meta.env.VITE_API_URL] : [])],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    beforeSend(event, hint) {
      // Stale hashed chunks (recovered by chunk-reload.js) and scripts injected by browser
      // extensions / in-app browsers are not our bugs.
      if (isIgnorableBrowserError(sentryEventMessage(event, hint?.originalException))) return null
      return event
    },
  })

  // Who this deployment serves: the Hospora organisation is named by VITE_HOSPORA_ORG when set, else by the API
  // (which is bound to one organisation: GET /config -> org.slug). The org store swallows network errors, so an
  // unreachable API simply leaves the slug empty and we fall through to the "not configured" message below.
  const org = useOrgStore(pinia)
  const slug = sharedOrgSlug()
  if (slug) {
    setHosporaOrg(slug)
    void org.load()
  } else {
    await org.load()
    setHosporaOrg(org.slug)
  }

  let hosporaOrgSlug: string
  try {
    hosporaOrgSlug = hosporaOrg()
  } catch (e) {
    // Suspended for an unpaid invoice: the very first call (GET /config) was refused, so the slug is unknown. That is
    // not a configuration fault; show the suspended page (App.vue) and stop.
    if (suspension.value) {
      launchScreen?.unmount()
      app.mount('#app')
      return
    }
    // Fail loudly: never guess an organisation. (In development this is an error in the console too.)
    const message = e instanceof HosporaOrgMissingError ? e.message : String(e)
    document.getElementById('app')!.innerHTML = `<div style="font-family:system-ui,sans-serif;max-width:32rem;margin:15vh auto;padding:0 1rem;line-height:1.5"><h1 style="font-size:1.25rem">This app is not configured</h1><p>${message.replace(/[<>&]/g, '')}</p><p><button onclick="location.reload()">Try again</button></p></div>`
    if (import.meta.env.DEV) throw e
    return
  }

  // Hospora theme: the organisation's brand colour, the product switcher and the light theme. The org is the one
  // this deployment serves, the same one used for "Sign in with Hospora" (LoginView mounts only after this).
  void hospora.setOrg(hosporaOrgSlug)
  // public/chunk-reload.js owns stale-chunk recovery. Allow a future recovery only after a
  // route actually loads, never while the replacement assets are still failing.
  router.afterEach((_to, _from, failure) => {
    if (failure) return
    try { sessionStorage.removeItem(storageKey('chunk-reload-attempted')) } catch { /* storage denied */ }
  })
  app.use(router)
  await router.isReady()
  launchScreen?.unmount()
  app.mount('#app')
}

void boot()
